import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { getFeaturedBlockchain } from "@/app/blockchains/featured-blockchain/get-featured-blockchain"
import { CurrencyMoleculeReatom } from "@/app/currency/store/index.reatom"
import { BlockchainBalanceSdkProviderMoleculeReatom } from "@/app/sdk/blockchains/blockchain-balance-sdk-provider.reatom"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { Blockchain as BlockchainType } from "@anaconda/core/blockchain/types"
import type { CurrencyConfig } from "@anaconda/core/currency/types"
import type { Currency } from "@anaconda/core/price/types"
import { UNKNOWN_CURRENCY } from "@anaconda/core/price/types"
import type { Address } from "@rarible_int/anaconda_sdk/ethereum"
import { atom, computed } from "@reatom/core"
import { createScope, molecule } from "bunshi/react"

function currencyConfigToCurrency(config: CurrencyConfig): Currency {
  return {
    id: config.id,
    abbreviation: config.symbol,
  }
}

function resolveCurrency(
  currencyById: (currencyId?: Currency["id"]) => Currency,
  configCurrency?: CurrencyConfig,
): Currency {
  const resolved = currencyById(configCurrency?.id)

  if (resolved.id !== UNKNOWN_CURRENCY.id) {
    return resolved
  }

  return configCurrency ? currencyConfigToCurrency(configCurrency) : resolved
}

export const BalanceBlockchainScope = createScope<BlockchainType>(
  getFeaturedBlockchain(),
)

export type BalanceMode = "deposit-to-bid-balance" | "withdraw-from-bid-balance"

export const BalanceMoleculeReatom = molecule((mol, scope) => {
  const {
    evmAddress,
    solanaAddress,
    walletBlockchainNetworkId,
    getBalanceAtom,
    getPublicClient: getPublicClientAtom,
  } = mol(WalletConnectMoleculeReatom)
  const { reatomWrappedCurrencyId, reatomDefaultCurrencyId, blockchainsList } =
    mol(BlockchainsMoleculeReatom)
  const { reatomCurrencyById } = mol(CurrencyMoleculeReatom)
  const { balanceSDKAtom } = mol(BlockchainBalanceSdkProviderMoleculeReatom)
  const blockchain = scope(BalanceBlockchainScope)

  const selectedNetworkAtom = computed(() => {
    const blockchains = blockchainsList()
    const blockchainConfig = blockchains.find(
      (b) => b.blockchain === blockchain,
    )
    return blockchainConfig?.chainId || blockchain
  }, "selectedNetwork")

  const isSelectedBlockchainConnectedAtom = computed(() => {
    const selectedChainId = selectedNetworkAtom()
    const connectedChainId = walletBlockchainNetworkId()
    const isMatch = selectedChainId === connectedChainId
    return isMatch
  }, "isSelectedBlockchainConnected")

  // Query params for native balance
  const nativeBalanceQueryParamsAtom = computed(() => {
    const selectedNetwork = selectedNetworkAtom()
    const evmAddr = evmAddress()
    const solanaAddr = solanaAddress()
    const addr = blockchain === Blockchain.SOLANA ? solanaAddr : evmAddr
    const balanceSDK = balanceSDKAtom()

    return {
      queryKey: ["nativeBalance", selectedNetwork, addr, blockchain] as const,
      queryFn: async () => {
        if (!addr) return "0"

        try {
          if (!balanceSDK) {
            return "0"
          }

          const result = await balanceSDK.getNativeBalance({
            blockchain,
            address: addr as Address,
          })
          return result
        } catch (e) {
          // Fallback: if selected blockchain matches connected wallet and RPC fails,
          // try to get balance from wallet connection
          const isConnectedBlockchain = isSelectedBlockchainConnectedAtom()

          if (isConnectedBlockchain) {
            try {
              const { getBalance } = getBalanceAtom()
              const walletBalance = await getBalance()
              return walletBalance || "0"
            } catch (walletError) {
              return "0"
            }
          }

          return "0"
        }
      },
      refetchInterval: 5000,
    }
  }, "nativeBalanceQueryParams")

  // Query params for wrapped balance
  const wrappedBalanceQueryParamsAtom = computed(() => {
    const selectedNetwork = selectedNetworkAtom()
    const addr = evmAddress()
    const balanceSDK = balanceSDKAtom()
    const wrappedCurrencyGetter = reatomWrappedCurrencyId()
    const wrappedCurrency = wrappedCurrencyGetter(blockchain)

    return {
      queryKey: ["wrappedBalance", selectedNetwork, addr, blockchain] as const,
      queryFn: async () => {
        if (!addr || !wrappedCurrency || blockchain === Blockchain.SOLANA) {
          return ""
        }

        try {
          if (!balanceSDK) {
            return ""
          }

          const result = await balanceSDK.getTokenBalanceByCurrency({
            blockchain,
            address: addr as Address,
            currencyId: wrappedCurrency.id,
          })
          return result
        } catch (e) {
          return "0"
        }
      },
      refetchInterval: 5000,
      retry: () => {
        return false
      },
      retryOnMount: false,
      refetchOnWindowFocus: false,
    }
  }, "wrappedBalanceQueryParams")

  // Get native currency info (requires currencies array from query)
  const getNativeCurrency = (currencies: Currency[] | undefined) => {
    const defaultCurrencyGetter = reatomDefaultCurrencyId()
    const configCurrency = defaultCurrencyGetter(blockchain)
    const currencyByIdGetter = reatomCurrencyById(currencies)

    return resolveCurrency(currencyByIdGetter, configCurrency)
  }

  // Get wrapped currency info (requires currencies array from query)
  const getWrappedCurrency = (currencies: Currency[] | undefined) => {
    const wrappedCurrencyGetter = reatomWrappedCurrencyId()
    const configCurrency = wrappedCurrencyGetter(blockchain)
    const currencyByIdGetter = reatomCurrencyById(currencies)

    return resolveCurrency(currencyByIdGetter, configCurrency)
  }

  // Balance mode state
  const balanceModeAtom = atom<BalanceMode>(
    "deposit-to-bid-balance",
    "balanceMode",
  )

  const switchBalanceMode = (balanceToSwitch?: BalanceMode) => {
    if (balanceToSwitch === undefined) {
      const balanceMode = balanceModeAtom()
      if (balanceMode === "deposit-to-bid-balance") {
        balanceModeAtom.set("withdraw-from-bid-balance")
      } else if (balanceMode === "withdraw-from-bid-balance") {
        balanceModeAtom.set("deposit-to-bid-balance")
      }
      return
    }
    balanceModeAtom.set(balanceToSwitch)
  }

  const isDepositAtom = computed(
    () => balanceModeAtom() === "deposit-to-bid-balance",
    "isDeposit",
  )

  const isWithdrawAtom = computed(
    () => balanceModeAtom() === "withdraw-from-bid-balance",
    "isWithdraw",
  )

  return {
    blockchain,
    selectedNetworkAtom,
    isSelectedBlockchainConnectedAtom,
    getNativeCurrency,
    getWrappedCurrency,
    nativeBalanceQueryParamsAtom,
    wrappedBalanceQueryParamsAtom,
    balanceModeAtom,
    switchBalanceMode,
    isDepositAtom,
    isWithdrawAtom,
  }
})
